import { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, ChevronDown, CreditCard, FileText, QrCode, ShoppingBag, ArrowRight } from 'lucide-react'
import logo from '../assets/logo.png'

const productsMenu = [
  {
    icon: <CreditCard className="h-5 w-5 text-orange-500" />,
    name: 'Payment Gateway',
    description: 'Secure online checkout for e-commerce',
    href: '/products',
  },
  {
    icon: <FileText className="h-5 w-5 text-orange-500" />,
    name: 'Bill Payments',
    description: 'Issue invoices & collect via all banks',
    href: '/products',
  },
  {
    icon: <QrCode className="h-5 w-5 text-orange-500" />,
    name: 'Mobile Payments',
    description: 'QR & Tap-to-Pay with UnionPay',
    href: '/products',
  },
  {
    icon: <ShoppingBag className="h-5 w-5 text-orange-500" />,
    name: 'E-Commerce Plugins',
    description: 'WooCommerce, Shopify & more',
    href: '/products',
  },
]

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [productsOpen, setProductsOpen] = useState(false)
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const location = useLocation()

  const isActive = (path: string) => location.pathname === path
  const isProductsActive = location.pathname === '/products'

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setProductsOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false)
    setProductsOpen(false)
  }, [location.pathname])

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="container-max section-padding">
        <div className="flex items-center justify-between h-16 lg:h-18">

          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img src={logo} alt="MobiPay" className="h-9 w-auto" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center space-x-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                isActive('/') ? 'bg-orange-50 text-orange-600' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Home
            </Link>

            {/* Products dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setProductsOpen((v) => !v)}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  isProductsActive || productsOpen
                    ? 'bg-orange-50 text-orange-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Products
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${productsOpen ? 'rotate-180' : ''}`} />
              </button>

              {productsOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
                  <div className="p-3">
                    {productsMenu.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setProductsOpen(false)}
                        className="flex items-start gap-3 p-3 rounded-xl hover:bg-orange-50 transition-colors group"
                      >
                        <div className="flex-shrink-0 w-9 h-9 bg-orange-50 group-hover:bg-orange-100 rounded-lg flex items-center justify-center transition-colors">
                          {item.icon}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 text-sm group-hover:text-orange-600 transition-colors">
                            {item.name}
                          </p>
                          <p className="text-gray-400 text-xs mt-0.5">{item.description}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                  <div className="border-t border-gray-100 p-3">
                    <Link
                      to="/products"
                      onClick={() => setProductsOpen(false)}
                      className="flex items-center justify-center gap-2 w-full bg-orange-500 hover:bg-orange-600 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors"
                    >
                      View all products <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                isActive('/about') ? 'bg-orange-50 text-orange-600' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              About Us
            </Link>

            <Link
              to="/faqs"
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                isActive('/faqs') ? 'bg-orange-50 text-orange-600' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              FAQs
            </Link>

            <Link
              to="/contact"
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                isActive('/contact') ? 'bg-orange-50 text-orange-600' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Contact
            </Link>
          </nav>

          {/* Right actions */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:+923438018055"
              className="text-gray-500 hover:text-orange-600 text-sm font-medium transition-colors"
            >
              +92 343 8018055
            </a>
            <Link
              to="/get-started"
              className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 shadow-md hover:shadow-lg hover:shadow-orange-500/25"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-gray-200 bg-white">
          <nav className="container-max section-padding py-4 flex flex-col gap-1">
            <Link
              to="/"
              className={`px-4 py-3 rounded-xl font-medium text-sm ${
                isActive('/') ? 'bg-orange-50 text-orange-600' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Home
            </Link>

            {/* Products accordion */}
            <div>
              <button
                onClick={() => setMobileProductsOpen((v) => !v)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-medium text-sm ${
                  isProductsActive ? 'bg-orange-50 text-orange-600' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Products
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileProductsOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileProductsOpen && (
                <div className="mt-1 ml-4 pl-4 border-l-2 border-orange-100 flex flex-col gap-1">
                  {productsMenu.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-600 hover:text-orange-600 hover:bg-orange-50 text-sm transition-colors"
                    >
                      {item.icon}
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`px-4 py-3 rounded-xl font-medium text-sm ${
                isActive('/about') ? 'bg-orange-50 text-orange-600' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              About Us
            </Link>

            <Link
              to="/faqs"
              className={`px-4 py-3 rounded-xl font-medium text-sm ${
                isActive('/faqs') ? 'bg-orange-50 text-orange-600' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              FAQs
            </Link>

            <Link
              to="/contact"
              className={`px-4 py-3 rounded-xl font-medium text-sm ${
                isActive('/contact') ? 'bg-orange-50 text-orange-600' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Contact
            </Link>

            <div className="pt-3 border-t border-gray-100 mt-2">
              <Link
                to="/get-started"
                className="block w-full bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 rounded-xl font-semibold text-sm text-center transition-colors"
              >
                Get Started
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}

export default Header
